<script lang="ts">
  import type { Snippet } from 'svelte';
  import { animateOverlay } from '../../game/motion';

  interface Props {
    title: string;
    /** Body copy. Newlines are honoured, like the view placeholders. */
    message?: string;
    /**
     * The way out that changes nothing: the backdrop and Escape both take it.
     * A modal with no cheap escape is a modal players learn to dread.
     */
    oncancel: () => void;
    /** The actions. Ordered most affirmative first, as everywhere else. */
    children: Snippet;
  }

  let { title, message, oncancel, children }: Props = $props();

  let panel: HTMLDivElement | undefined = $state();

  /**
   * Focus moves into the dialog on open and Escape closes it. Both are the
   * platform's own behaviour for a modal and neither survives being styled as
   * a pixel panel unless it is written down.
   */
  $effect(() => {
    panel?.querySelector<HTMLButtonElement>('button')?.focus();
  });

  function onkeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      oncancel();
      return;
    }

    // Tab cycles inside the dialog: behind it is a screen the player has been
    // told they cannot leave yet, and letting focus land there would be a lie.
    if (event.key !== 'Tab' || !panel) return;

    const focusable = [...panel.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
</script>

<svelte:window {onkeydown} />

<!--
  The backdrop is a plain element with a click handler rather than a button:
  it is not a control, it is the rest of the screen — announcing it as an
  action would put a nameless button in front of every dialog.
-->
<div
  class="dialog"
  role="presentation"
  onclick={(event) => event.target === event.currentTarget && oncancel()}
  use:animateOverlay={{ emphasis: true }}
>
  <div
    class="panel panel--accent dialog__panel"
    role="dialog"
    aria-modal="true"
    aria-label={title}
    bind:this={panel}
  >
    <strong class="dialog__title">{title}</strong>
    {#if message}<p class="dialog__message">{message}</p>{/if}
    <div class="dialog__actions">
      {@render children()}
    </div>
  </div>
</div>
